import {
  type KeyboardEvent,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { useSnapCarousel } from '../shared/useSnapCarousel'
import { AiLabsCard } from './AiLabsCard'
import type { AiLabsCardItem, AiLabsCarouselHandle } from './types'

interface AiLabsCarouselProps {
  items: AiLabsCardItem[]
  sectionLabel: string
  infinite?: boolean
  /** Centered card changed — drives the bg cross-fade. */
  onActiveIndexChange?: (index: number) => void
}

// Auto-advance interval (design-specified).
const AUTO_ROTATE_MS = 7000
// Fallback for browsers without `scrollend` to release the in-flight guard.
const SCROLL_SETTLE_MS = 700
const INFINITE_SET_COUNT = 3

/**
 * Scroll-snap row driven by the header arrows, arrow keys, and a looping
 * auto-rotation timer. Auto-rotation pauses on hover/focus, while the row is
 * off-screen or the tab is hidden, and holds off for one interval after any
 * user interaction so it never fights a gesture. Tracks the centered card and
 * reports it up for the section bg cross-fade.
 */
export const AiLabsCarousel = forwardRef<
  AiLabsCarouselHandle,
  AiLabsCarouselProps
>(({ items, sectionLabel, infinite = false, onActiveIndexChange }, ref) => {
  const isInfinite = infinite && items.length > 1
  // True while a programmatic scroll settles; the auto-tick skips it.
  const scrollingRef = useRef(false)
  const settleTimerRef = useRef<number | undefined>(undefined)
  // Last user interaction; the auto-tick waits one interval after it.
  const lastInteractionRef = useRef(0)
  const [activeIndex, setActiveIndex] = useState(0)
  // Hover and focus pause independently (touch fires pointercancel, not leave).
  const [hovered, setHovered] = useState(false)
  const [focused, setFocused] = useState(false)
  const [inView, setInView] = useState(true)
  const [pageVisible, setPageVisible] = useState(true)
  const paused = hovered || focused || !inView || !pageVisible

  const recenterRef = useRef<() => void>(() => {})

  // Active = card centre nearest the viewport centre.
  const syncActive = useCallback((root: HTMLDivElement) => {
    const rootRect = root.getBoundingClientRect()
    const rootCenter = rootRect.left + rootRect.width / 2
    let bestIndex = 0
    let bestDistance = Infinity
    Array.from(root.children).forEach((child, index) => {
      const rect = child.getBoundingClientRect()
      const center = rect.left + rect.width / 2
      const distance = Math.abs(center - rootCenter)
      if (distance < bestDistance) {
        bestDistance = distance
        bestIndex = index
      }
    })
    setActiveIndex((prev) => (prev === bestIndex ? prev : bestIndex))
  }, [])

  // User-initiated scroll (not our own programmatic one) counts as interaction.
  const handleScroll = useCallback(() => {
    if (!scrollingRef.current) lastInteractionRef.current = Date.now()
  }, [])

  // `scrollend` (or the settle-timer fallback) releases the in-flight guard.
  const releaseScroll = useCallback(() => {
    recenterRef.current()
    scrollingRef.current = false
    if (settleTimerRef.current != null) {
      window.clearTimeout(settleTimerRef.current)
      settleTimerRef.current = undefined
    }
  }, [])

  // Mark a programmatic scroll in flight; `scrollend` clears it, timeout is a fallback.
  const beginScroll = useCallback(() => {
    scrollingRef.current = true
    if (settleTimerRef.current != null)
      window.clearTimeout(settleTimerRef.current)
    settleTimerRef.current = window.setTimeout(() => {
      recenterRef.current()
      scrollingRef.current = false
      settleTimerRef.current = undefined
    }, SCROLL_SETTLE_MS)
  }, [])

  const { scrollRef, prefersReducedMotion, scrollByCard, scrollToOffset } =
    useSnapCarousel({
      sync: syncActive,
      onScroll: handleScroll,
      onScrollEnd: releaseScroll,
      beforeProgrammaticScroll: beginScroll,
    })

  const getSetWidth = useCallback(() => {
    const el = scrollRef.current
    if (!el || !isInfinite) return 0
    const first = el.children.item(0) as HTMLElement | null
    const nextSetFirst = el.children.item(items.length) as HTMLElement | null
    if (!first || !nextSetFirst) return 0
    return nextSetFirst.offsetLeft - first.offsetLeft
  }, [isInfinite, items.length, scrollRef])

  const recenter = useCallback(() => {
    const el = scrollRef.current
    if (!el || !isInfinite) return
    const setWidth = getSetWidth()
    if (setWidth <= 0) return
    if (el.scrollLeft >= setWidth * 2) el.scrollLeft -= setWidth
    else if (el.scrollLeft < setWidth) el.scrollLeft += setWidth
  }, [getSetWidth, isInfinite, scrollRef])
  recenterRef.current = recenter

  useEffect(() => {
    const el = scrollRef.current
    if (!el || !isInfinite) return
    const setWidth = getSetWidth()
    if (setWidth <= 0) return
    beginScroll()
    el.scrollLeft = setWidth
  }, [beginScroll, getSetWidth, isInfinite, scrollRef])

  useEffect(() => {
    const realIndex = items.length ? activeIndex % items.length : 0
    onActiveIndexChange?.(realIndex)
  }, [activeIndex, items.length, onActiveIndexChange])

  // Clear the settle-timer fallback on unmount.
  useEffect(
    () => () => {
      if (settleTimerRef.current != null)
        window.clearTimeout(settleTimerRef.current)
    },
    [],
  )

  const goToNext = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    if (isInfinite) {
      scrollByCard(1)
      return
    }
    const hasOverflow = el.scrollWidth > el.clientWidth
    // Loop to first at the end.
    if (hasOverflow && el.scrollLeft + el.clientWidth >= el.scrollWidth - 1) {
      scrollToOffset(0)
    } else {
      scrollByCard(1)
    }
  }, [isInfinite, scrollByCard, scrollToOffset, scrollRef])

  const goToPrev = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    if (isInfinite) {
      scrollByCard(-1)
      return
    }
    const hasOverflow = el.scrollWidth > el.clientWidth
    if (hasOverflow && el.scrollLeft <= 1) {
      scrollToOffset(el.scrollWidth)
    } else {
      scrollByCard(-1)
    }
  }, [isInfinite, scrollByCard, scrollToOffset, scrollRef])

  // Manual nav resets the auto cadence so the next tick doesn't double-advance.
  const navNext = useCallback(() => {
    lastInteractionRef.current = Date.now()
    goToNext()
  }, [goToNext])

  const navPrev = useCallback(() => {
    lastInteractionRef.current = Date.now()
    goToPrev()
  }, [goToPrev])

  useImperativeHandle(ref, () => ({ goToPrev: navPrev, goToNext: navNext }), [
    navPrev,
    navNext,
  ])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const el = scrollRef.current
      if (!el) return
      if (event.key === 'ArrowRight') {
        event.preventDefault()
        navNext()
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault()
        navPrev()
      } else if (event.key === 'Home') {
        event.preventDefault()
        lastInteractionRef.current = Date.now()
        scrollToOffset(0)
      } else if (event.key === 'End') {
        event.preventDefault()
        lastInteractionRef.current = Date.now()
        scrollToOffset(el.scrollWidth)
      }
    },
    [navNext, navPrev, scrollToOffset, scrollRef],
  )

  // Refs so callback identity doesn't restart the interval on every scroll.
  const goToNextRef = useRef(goToNext)
  goToNextRef.current = goToNext

  useEffect(() => {
    const el = scrollRef.current
    if (!el || typeof IntersectionObserver === 'undefined') return undefined
    const observer = new IntersectionObserver(([entry]) =>
      setInView(entry.isIntersecting),
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [scrollRef])

  useEffect(() => {
    if (typeof document === 'undefined') return undefined
    const update = () => setPageVisible(document.visibilityState === 'visible')
    update()
    document.addEventListener('visibilitychange', update)
    return () => document.removeEventListener('visibilitychange', update)
  }, [])

  useEffect(() => {
    if (prefersReducedMotion || paused || items.length <= 1) return undefined
    if (typeof window === 'undefined') return undefined
    const id = window.setInterval(() => {
      if (scrollingRef.current) return
      if (Date.now() - lastInteractionRef.current < AUTO_ROTATE_MS) return
      goToNextRef.current()
    }, AUTO_ROTATE_MS)
    return () => window.clearInterval(id)
  }, [prefersReducedMotion, paused, items.length])

  const setCount = isInfinite ? INFINITE_SET_COUNT : 1
  const liveSet = isInfinite ? 1 : 0

  // Keyboard-operable scrollable region (ARIA APG); jsx-a11y misflags it.
  /* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */
  return (
    <div
      ref={scrollRef}
      role='region'
      aria-label={sectionLabel}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      onPointerCancel={() => setHovered(false)}
      onPointerDown={() => {
        lastInteractionRef.current = Date.now()
      }}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={() => setFocused(false)}
      className='-mx-5 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 scroll-px-5 [scrollbar-width:none] focus-visible:outline-none md:mx-0 md:px-0 md:scroll-px-0 [&::-webkit-scrollbar]:hidden'
    >
      {Array.from({ length: setCount }).flatMap((_, setIndex) =>
        items.map((item, index) => (
          <AiLabsCard
            key={`${item.id}-${setIndex}`}
            {...item}
            isActive={setIndex * items.length + index === activeIndex}
            isDuplicate={setIndex !== liveSet}
          />
        )),
      )}
    </div>
  )
  /* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */
})

AiLabsCarousel.displayName = 'AiLabsCarousel'
